import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { UPDATE_BG_ACCOUNT } from '../../graphql/mutation'
import { GET_BG_ACCOUNT_BY_ID } from '../../graphql/query'
import { toAbsoluteUrl } from '../../helpers'
import * as Yup from 'yup'

interface IProps {
  closeBusinessModal?: () => void
  loading: any
  error: any
  data: any
  formData: any
  setFormData: any
}

const OnboardingSchema = Yup.object().shape({
  organization: Yup.string()
    .matches(/^[a-zA-Z0-9(),.\- ]*$/, 'Organization contains invalid characters')
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Organization is required'),
  Address: Yup.string()
    .matches(/^[a-zA-Z0-9(),.\- ]*$/, 'Address contains invalid characters')
    .min(2, 'Minimum 2 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Address is required'),
  DateOfEstablishment: Yup.date()
    .max(new Date(), 'Date of Incorporation cannot be in the future')
    .required('Date of Incorporation is required')
})

export const OrganizationDetails: React.FC<IProps> = ({
  closeBusinessModal,
  loading,
  error,
  data,
  formData,
  setFormData
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [updateBgAccount] = useMutation(UPDATE_BG_ACCOUNT, {
    refetchQueries: [
      {
        query: GET_BG_ACCOUNT_BY_ID,
        context: {
          headers: {
            'access-control-allow-methods': 'Profile'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })
  const [selectedOptions, setSelectedOptions] = useState({
    industryType: '',
    companyType: '',
    companySize: ''
  })
  const navigate = useNavigate()

  const handleDropdownSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value

    setSelectedOptions((prevValue) => ({
      ...prevValue,
      [name]: value
    }))
  }

  useEffect(() => {
    console.log(data)
    if (!!data && !!data.getBgAccountById.industry) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        industryType: data.getBgAccountById.industry
      }))
    }
    if (!!data && !!data.getBgAccountById.typeOfCompany) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        companyType: data.getBgAccountById.typeOfCompany
      }))
    }
    if (!!data && !!data.getBgAccountById.companySize) {
      setSelectedOptions((prevValue) => ({
        ...prevValue,
        companySize: data.getBgAccountById.companySize
      }))
    }
  }, [data])

  const initialValues = {
    organization:
      !!data && !loading && !error && data.getBgAccountById.organization
        ? data.getBgAccountById.organization
        : '',
    Address:
      !!data && !loading && !error && data.getBgAccountById.registeredAddress
        ? data.getBgAccountById.registeredAddress
        : '',

    DateOfEstablishment:
      !!data && !loading && !error && data.getBgAccountById.dateOfIncorporation
        ? new Date(data.getBgAccountById.dateOfIncorporation).toISOString().split('T')[0]
        : ''
  }
  console.log(initialValues)
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      console.log(values)
      const updatedFormData = {
        ...formData,
        organization: values.organization,
        registeredAddress: values.Address,
        typeOfCompany: selectedOptions.companyType,
        industry: selectedOptions.industryType,
        dateOfIncorporation: values.DateOfEstablishment,
        companySize: selectedOptions.companySize
      }
      setFormData(updatedFormData)
      updateBgAccount({
        variables: {
          input: {
            organization: values.organization,
            registeredAddress: values.Address,
            typeOfCompany: selectedOptions.companyType,
            industry: selectedOptions.industryType,
            dateOfIncorporation: values.DateOfEstablishment,
            companySize: selectedOptions.companySize
          }
        }
      })
        .then((res: any) => {
          console.log(res)
          if (res.data.updateBgAccount.status === 200) {
            toast.success(res.data.updateBgAccount.message)
            navigate('/onboarding/legal-information')
          } else {
            toast.error(res.data.updateBgAccount.message)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  })

  if (loading) {
    setIsLoading(loading)
  }

  if (error) {
    return <p>error</p>
  }

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }
  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')})`
      }}
    >
      <div className='d-flex flex-center flex-column flex-column-fluid p-6 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_signup_form'
            onSubmit={formik.handleSubmit}
          >
            {!closeBusinessModal && (
              <div className='mb-10 text-center'>
                <h2 className='text-dark mb-4'> Organization Details Page</h2>
                <p className='text-muted fs-5'>Tell us little bit about your business </p>
              </div>
            )}

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>Organization:</label>
              <input
                placeholder='Organization Name'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('organization')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.organization && formik.errors.organization
                  },
                  {
                    'is-valid': formik.touched.organization && !formik.errors.organization
                  }
                )}
              />
              {formik.touched.organization && formik.errors.organization && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.organization)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                Registered Address:
              </label>
              <input
                placeholder='Registered Address'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('Address')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.Address && formik.errors.Address
                  },
                  {
                    'is-valid': formik.touched.Address && !formik.errors.Address
                  }
                )}
              />
              {formik.touched.Address && formik.errors.Address && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.Address)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                What is the employee size of your company?
              </label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.companySize}
                  onChange={handleDropdownSelect}
                  name='companySize'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='belowTwenty'>Below 20</option>
                  <option value='twentyOneToFifty' selected>
                    21 to 50
                  </option>
                  <option value='fiftyOneToTwoHundred'>51 to 100</option>
                  <option value='twoHundredOneToFiveHundred'>201 to 500</option>
                  <option value='fiveHundredOneToThousand'>501 to 1000</option>
                  <option value='thousandPlus'>1000+</option>
                </select>
              </div>
            </div>

            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                What is your company type?
              </label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.companyType}
                  onChange={handleDropdownSelect}
                  name='companyType'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='privateLimitedCompany'>Private Limited Company (Pvt. Ltd.)</option>
                  <option value='publicLimitedCompany'>Public Limited Company (Ltd.)</option>
                  <option value='limitedLiabilityPartnership'>
                    Limited Liability Partnership (LLP)
                  </option>
                  <option value='limitedLiabilityCompany'>Limited Liability Company(LLC)</option>
                  <option value='soleProprietorship'>Sole Proprietorship</option>
                  <option value='partnership'>Partnership</option>
                  <option value='onePersonCompany'> One Person Company (OPC)</option>
                </select>
              </div>
            </div>

            <div className='mb-10 fv-row'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                What is your industry type?
              </label>
              <div className='position-relative mb-3'>
                <select
                  className=' form-select form-select-solid form-select '
                  value={selectedOptions.industryType}
                  onChange={handleDropdownSelect}
                  name='industryType'
                >
                  <option key='blankKey' hidden>
                    Select
                  </option>
                  <option value='Food & Beverages'>Food & Beverages</option>
                  <option value='Arts & Entertainment'>Arts & Entertainment</option>
                  <option value='Agency & Consulting'>Agency & Consulting</option>
                  <option value='Retail & E-commerce'>Retail & E-commerce</option>
                  <option value='Hospitality & Tourism'>Hospitality & Tourism</option>
                  <option value='Automative'> Automative</option>
                  <option value='Government & Nonprofit'>Government & Nonprofit</option>
                  <option value='Financial Services'>Financial Services</option>
                  <option value='Real Estate'>Real Estate</option>
                  <option value='Software & Technology'>Software & Technology</option>
                  <option value='Healthcare & Medical Services'>
                    Healthcare & Medical Services
                  </option>
                  <option value='Legal Services'>Legal Services</option>
                  <option value='Education'>Education</option>
                  <option value='Other'>Other</option>
                </select>
              </div>
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6 required'>
                Date of Incorporation:
              </label>
              <input
                placeholder='Date of Establishment'
                type='date'
                autoComplete='off'
                {...formik.getFieldProps('DateOfEstablishment')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid':
                      formik.touched.DateOfEstablishment && formik.errors.DateOfEstablishment
                  },
                  {
                    'is-valid':
                      formik.touched.DateOfEstablishment && !formik.errors.DateOfEstablishment
                  }
                )}
              />
              {formik.touched.DateOfEstablishment && formik.errors.DateOfEstablishment && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.DateOfEstablishment}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={
                  formik.isSubmitting ||
                  !formik.isValid ||
                  !selectedOptions.companySize ||
                  !selectedOptions.companyType ||
                  !selectedOptions.industryType
                }
              >
                {!isLoading && <span className='indicator-label'>Next</span>}
                {isLoading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
              {!closeBusinessModal && (
                <Link to='/onboarding/personal-details'>
                  <button
                    type='button'
                    id='kt_login_signup_form_cancel_button'
                    className='btn btn-lg btn-secondary w-100 mb-5'
                  >
                    Previous
                  </button>
                </Link>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
export default OrganizationDetails
